import {
  ModalTitle,
  ModalSubtitle,
  StyledInput,
  VerificationWrapper,
  AgentLoginFormWrapper,
  WhatsAppInfo,
} from "./Styles";

import { Modal } from "../../../../Components/Modal";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../Common/Hooks/useAuth";
import { isErr } from "../../../../Common/Utils/Result";
import { eventBus } from "../../../../Common/EventBus";
import { Notification } from "../../../../Components/Notifications";
import { LoginIntent } from "../../../../Common/Types-DTOs/LoginIntent";
import { CustomPhoneInput } from "../../../../Components/CustomPhoneInput";
import { LoginButton, NavigateParagraph } from "../../Styles";
import { HiArrowRight } from "react-icons/hi2";
import WhatsAppIcon from "../../../../Assets/whatsapp.png";


export const AgentLogin = ({
  display,
  onClose
}: {
  display: boolean;
  onClose?: () => void;
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [loginIntent, setLoginIntent] = useState<LoginIntent | undefined>(
    undefined
  );

  const { tryAgentLogin, agentLogin } = useAuth();
  const navigate = useNavigate();

  const handleClose = () => {
    setStep(1);
    setPhone("");
    setLoginIntent(undefined);
    if (onClose) {
      onClose();
    }
  };

  const submmitPhone = async () => {
    const response = await tryAgentLogin("34" + phone);

    if (isErr(response)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          t("auth.login.agent.errors.phoneNotFound")
        )
      );
      return;
    }
    setLoginIntent(response.value);

    setStep(2);
  };

  const submmitCode = async (code: string) => {
    if (!loginIntent) return;

    const response = await agentLogin(loginIntent.id, code);

    if (isErr(response)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          t("auth.login.agent.errors.invalidCode")
        )
      );
      return;
    }

    eventBus.emit(
      "NOTIFICATION",
      Notification.buildNotification("success", t("auth.login.agent.welcome"))
    );

    handleClose();
    navigate("/");
  };

  return (
    <Modal isOpen={display} onClose={handleClose}>
      {step === 1 && (
        <Step1 submmit={submmitPhone} setPhone={setPhone} phone={phone} onClose={handleClose} />
      )}
      {step === 2 && <Step2 submmit={submmitCode} onClose={handleClose} />}
    </Modal>
  );
};

const Step1 = ({
  submmit,
  phone,
  setPhone,
  onClose,
}: {
  submmit: () => void;
  phone: string;
  setPhone: React.Dispatch<React.SetStateAction<string>>;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <AgentLoginFormWrapper>
      <WhatsAppInfo>
        <img src={WhatsAppIcon} alt={t("auth.login.whatsappLogoAlt")} height={24} />
        <span>{t("auth.login.agent.title")}</span>
      </WhatsAppInfo>
      <ModalTitle>{t("auth.login.agent.step1Title")}</ModalTitle>
      <ModalSubtitle>{t("auth.login.agent.step1Subtitle")}</ModalSubtitle>
      <CustomPhoneInput placeholder={t("auth.login.agent.phonePlaceholder")} value={phone} onChange={(phoneNumber) => setPhone(phoneNumber)} />
      <LoginButton onClick={submmit}>
        <span>{t("auth.login.agent.nextButton")}</span>
        <HiArrowRight />
      </LoginButton>
      <NavigateParagraph>
        <b onClick={onClose}>{t("auth.login.agent.backButton")}</b>
      </NavigateParagraph>
    </AgentLoginFormWrapper>
  );
};

export const Step2 = ({
  submmit,
  onClose
}: {
  submmit: (code: string) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [digits, setDigits] = useState(["", "", "", ""]);

  const handleVerification = () => {
    const code = digits.join("");
    if (code.length < 4) return;

    submmit(code);
  };

  return (
    <AgentLoginFormWrapper>
      <WhatsAppInfo>
        <img src={WhatsAppIcon} alt={t("auth.login.whatsappLogoAlt")} height={24} />
        <span>{t("auth.login.agent.title")}</span>
      </WhatsAppInfo>
      <ModalTitle>{t("auth.login.agent.step2Title")}</ModalTitle>
      <ModalSubtitle>
        {t("auth.login.agent.step2Subtitle")}
      </ModalSubtitle>
      <VerificationInput digits={digits} setDigits={setDigits} />
      <LoginButton onClick={handleVerification}>
        <span>{t("auth.login.agent.confirmButton")}</span>
        <HiArrowRight />
      </LoginButton>
      <NavigateParagraph>
        <b onClick={onClose}>{t("auth.login.agent.backButton")}</b>
      </NavigateParagraph>
    </AgentLoginFormWrapper>
  );
};

const VerificationInput = ({
  digits,
  setDigits,
}: {
  digits: string[];
  setDigits: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  useEffect(() => {
    inputRefs[0].current?.focus();
  }, []);

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handleChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    const newDigits = [...digits];

    if (value === "" || value.match(/^[0-9]$/)) {
      newDigits[index] = value;
      setDigits(newDigits);

      if (value && index < 3) {
        inputRefs[index + 1].current?.focus();
      }
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select();
  };

  return (
    <VerificationWrapper>
      {digits.map((digit, index) => (
        <StyledInput
          key={index}
          ref={inputRefs[index]}
          type="text"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={handleFocus}
        />
      ))}
    </VerificationWrapper>
  );
};
